import { useEffect, useState } from 'react';
import { Button, Drawer, Form, Input, Popconfirm, Space, Switch, Table, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import { deleteAiServiceConfig, getAiServiceConfigs, saveAiServiceConfig } from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;
const EMPTY = { serviceKey: '', label: '', baseUrl: '', authHeader: '', authValue: '', enabled: true };

function ServiceEditor({ open, service, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ ...EMPTY, ...service, authValue: '' });
  }, [open, service, form]);

  async function save() {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await saveAiServiceConfig(values);
      message.success('AI service saved');
      onSaved();
    } catch (error) {
      if (!error?.errorFields) message.error(error?.message || 'Could not save AI service');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="min(560px, 96vw)"
      title={service ? 'Edit AI service' : 'New AI service'}
      destroyOnClose
      extra={<Space><Button onClick={onClose}>Cancel</Button><Button type="primary" loading={saving} onClick={save}>Save</Button></Space>}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY}>
        <Form.Item name="serviceKey" label="Service Key" rules={[{ required: true, message: 'Service key is required' }]}
          tooltip="Referenced by an AI Action's Service Key on any form field (e.g. pyOps).">
          <Input placeholder="pyOps" disabled={Boolean(service)} className="fg-mono-input" />
        </Form.Item>
        <Form.Item name="label" label="Label"><Input placeholder="Resume/JD Parsing Service" /></Form.Item>
        <Form.Item name="baseUrl" label="Base URL" rules={[{ required: true, message: 'Base URL is required' }]}>
          <Input placeholder="https://ai.example.com" className="fg-mono-input" />
        </Form.Item>
        <Form.Item name="authHeader" label="Auth Header (optional)"><Input placeholder="x-api-key" className="fg-mono-input" /></Form.Item>
        <Form.Item name="authValue" label="Auth Value" tooltip="Write-only — leave blank to keep the currently saved value.">
          <Input.Password placeholder={service ? 'Unchanged' : ''} />
        </Form.Item>
        <Form.Item name="enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
      </Form>
    </Drawer>
  );
}

export default function AiServiceSettingsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    setLoading(true);
    getAiServiceConfigs()
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch((error) => message.error(error?.message || 'Could not load AI services'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function toggleEnabled(record, enabled) {
    try {
      await saveAiServiceConfig({ ...record, enabled });
      setItems((current) => current.map((item) => item.serviceKey === record.serviceKey ? { ...item, enabled } : item));
    } catch (error) {
      message.error(error?.message || 'Could not update AI service');
    }
  }

  async function remove(serviceKey) {
    try {
      await deleteAiServiceConfig(serviceKey);
      message.success('AI service deleted');
      load();
    } catch (error) {
      message.error(error?.message || 'Could not delete AI service');
    }
  }

  const columns = [
    {
      title: 'Service', key: 'service', render: (_, record) => (
        <div className="admin-config-primary-cell">
          <Text strong>{record.label || record.serviceKey}</Text>
          <Text type="secondary" code>{record.serviceKey}</Text>
        </div>
      ),
    },
    { title: 'Base URL', dataIndex: 'baseUrl', key: 'baseUrl', ellipsis: true, render: (value) => <Text code ellipsis={{ tooltip: value }}>{value}</Text> },
    { title: 'Auth Header', dataIndex: 'authHeader', key: 'authHeader', width: 140, render: (value) => value || '—' },
    { title: 'Enabled', dataIndex: 'enabled', key: 'enabled', width: 90, render: (value, record) => <Switch size="small" checked={value} onChange={(checked) => toggleEnabled(record, checked)} /> },
    {
      title: '', key: 'actions', width: 84, fixed: 'right', render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Edit"><Button type="text" icon={<EditOutlined />} onClick={() => { setEditing(record); setEditorOpen(true); }} /></Tooltip>
          <Popconfirm title="Delete this AI service?" onConfirm={() => remove(record.serviceKey)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard
        icon={<ThunderboltOutlined />}
        title="AI Services"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true); }}>New service</Button>}
      >
        <div className="admin-accent-bar" />
        <Text type="secondary">
          Register the AI/automation backends that admin-configured "AI Actions" (Generate/Parse-style buttons on
          any form field) call. A field's AI Action names a Service Key here — no URL is ever hardcoded in the app.
        </Text>
      </AppCard>
      <AppCard title={`${items.length} service${items.length === 1 ? '' : 's'}`} extra={<Button icon={<ReloadOutlined />} onClick={load}>Reload</Button>}>
        <Table rowKey="serviceKey" columns={columns} dataSource={items} loading={loading} className="fg-table" size="small" />
      </AppCard>
      <ServiceEditor open={editorOpen} service={editing} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}
